import { useState } from "react";
import { PatientVisit } from "../../../../amplify/api-gateway/search-patients";
import { callWrite, LLM_DASHBOARD_UPLOAD_CONTEXT } from "../../../../amplify/api-gateway/write";
import { dashboardLLMCache } from "../cache";

interface ContextItem {
    text: string;
    enabled: boolean;
}

interface ContextData {
    contexts: ContextItem[];
}

/**
 * Custom hook for managing additional context for LLM dashboard
 * Extracted from AdditionalContextModal for reusability
 */
export const useContextManagement = (patientVisit: PatientVisit, tenant: string) => {
    const [context, setContext] = useState<ContextData>({ contexts: [] });
    const [origContext, setOrigContext] = useState<ContextData>({ contexts: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    /**
     * Load context from cache
     */
    const loadContext = async () => {
        setIsLoading(true);
        try {
            const existingContext = await dashboardLLMCache.get("context", patientVisit, tenant);
            if (existingContext) {
                setOrigContext(JSON.parse(JSON.stringify(existingContext)));
                setContext(existingContext);
            }
        } catch (error) {
            console.error("Could not load context", error);
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Save context to backend
     */
    const saveContext = async (updatedContext: ContextData) => {
        setIsSaving(true);
        try {
            await callWrite({
                operation: LLM_DASHBOARD_UPLOAD_CONTEXT,
                csn: patientVisit.csn,
                mrn: patientVisit.mrn,
                data: updatedContext,
            });
            setContext(updatedContext);
            setOrigContext(updatedContext);
            return true;
        } catch (error) {
            console.error("Could not save context", error);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Toggle context item enabled/disabled state
     */
    const toggleContext = (idx: number) => {
        const updatedContext: ContextData = JSON.parse(JSON.stringify(context));
        if (idx < context.contexts.length) {
            updatedContext.contexts[idx].enabled = !updatedContext.contexts[idx].enabled;
        }
        setContext(updatedContext);
    };

    /**
     * Delete context item at index
     */
    const deleteContext = (idx: number) => {
        const updatedContext: ContextData = JSON.parse(JSON.stringify(context));
        if (idx < context.contexts.length) {
            updatedContext.contexts.splice(idx, 1);
        }
        setContext(updatedContext);
    };

    /**
     * Add new context item
     */
    const addContext = (text: string) => {
        const updatedContext: ContextData = JSON.parse(JSON.stringify(context));
        updatedContext.contexts.push({
            text,
            enabled: true,
        });
        setContext(updatedContext);
    };

    /**
     * Cancel edits and revert to original context
     */
    const cancelEdits = () => {
        setContext(JSON.parse(JSON.stringify(origContext)));
    };

    return {
        context,
        setContext,
        origContext,
        isLoading,
        isSaving,
        loadContext,
        saveContext,
        toggleContext,
        deleteContext,
        addContext,
        cancelEdits,
    };
};
